// =============================================================================
// staticSite/redirectsBuilder — plik _redirects i _headers dla Cloudflare Pages.
//
// Zrodla regul (priorytet: manual > wp_redirects > sitemap_gap > auto_wp):
//   auto_wp     — stale sciezki WordPressa, ktore na statycznej nie istnieja
//   wp_redirects — tabela wp_redirects (status applied) z modulu Istniejace
//   sitemap_gap — URL-e z live sitemap WP bez odpowiednika (propozycje z analizy)
//   manual      — wpisane recznie w panelu
// Limity CF Pages: 2000 regul statycznych + 100 dynamicznych (z * lub :splat).
// Query string (?p=123) nie da sie dopasowac — trafia na "/".
// =============================================================================

import type { SiteModel, StaticRedirectRow } from "./types";
import { normalizePath } from "./urlPolicy";

export const CF_STATIC_REDIRECTS_LIMIT = 2000;
export const CF_DYNAMIC_REDIRECTS_LIMIT = 100;

export function isDynamicRule(fromPath: string): boolean {
  return fromPath.includes("*") || /\/:[a-z]/i.test(fromPath);
}

/** Reguly, ktore ma KAZDA zmigrowana strona WP. */
export function buildAutoRedirects(site: SiteModel): StaticRedirectRow[] {
  const base = (site.settings.category_base || "category").replace(/^\/+|\/+$/g, "");
  const authorPath = site.author?.path || "/";
  const rows: StaticRedirectRow[] = [
    { from_path: "/wp-admin/*", to_path: "/", code: 301, source: "auto_wp", reason: "Panel WP nie istnieje" },
    { from_path: "/wp-login.php", to_path: "/", code: 301, source: "auto_wp", reason: "Logowanie WP nie istnieje" },
    { from_path: "/xmlrpc.php", to_path: "/", code: 301, source: "auto_wp", reason: "XML-RPC nie istnieje" },
    { from_path: "/wp-json/*", to_path: "/", code: 301, source: "auto_wp", reason: "REST API nie istnieje" },
    { from_path: "/page/*", to_path: "/", code: 301, source: "auto_wp", reason: "Paginacja home -> home" },
    { from_path: `/${base}/*/page/*`, to_path: "/", code: 301, source: "auto_wp", reason: "Paginacja kategorii -> home" },
    { from_path: "/tag/*", to_path: "/", code: 301, source: "auto_wp", reason: "Archiwa tagow -> home" },
    { from_path: "/author/*", to_path: authorPath, code: 301, source: "auto_wp", reason: "Archiwum autora -> strona autora" },
    { from_path: "/comments/feed/", to_path: "/feed/", code: 301, source: "auto_wp", reason: "Feed komentarzy -> feed" },
    { from_path: "/wp-sitemap.xml", to_path: "/sitemap.xml", code: 301, source: "auto_wp", reason: "Sitemap WP -> sitemap" },
    { from_path: "/sitemap_index.xml", to_path: "/sitemap.xml", code: 301, source: "auto_wp", reason: "Sitemap Yoast -> sitemap" },
  ];
  // Paginacja kategorii — po jednej regule per kategoria (celniej niz do home)
  for (const c of site.categories) {
    rows.push({
      from_path: `${c.path}page/*`,
      to_path: c.path,
      code: 301,
      source: "auto_wp",
      reason: `Paginacja kategorii ${c.name}`,
    });
  }
  return rows;
}

export interface WpRedirectRowLike {
  from_url: string;
  to_wp_post_url?: string | null;
  to_slug_planned?: string | null;
  status_code?: number | null;
  status?: string | null;
}

/** wp_redirects (applied) -> reguly. Sciezka docelowa z live URL-a albo ze sluga. */
export function redirectsFromWpRedirects(rows: WpRedirectRowLike[], site: SiteModel): StaticRedirectRow[] {
  const out: StaticRedirectRow[] = [];
  for (const r of rows) {
    if (r.status && r.status !== "applied") continue;
    const from = pathOf(r.from_url, site);
    const to = r.to_wp_post_url ? pathOf(r.to_wp_post_url, site) : r.to_slug_planned ? normalizePath(`/${r.to_slug_planned}/`) : null;
    if (!from || !to || from === to) continue;
    const code = [301, 302, 307, 308].includes(r.status_code || 0) ? (r.status_code as 301 | 302 | 307 | 308) : 301;
    out.push({ from_path: from, to_path: to, code, source: "wp_redirects", reason: "Z modulu Istniejace (wp_redirects)" });
  }
  return out;
}

function pathOf(url: string, site: SiteModel): string | null {
  try {
    const u = new URL(url.includes("://") ? url : `${site.baseUrl}${url.startsWith("/") ? "" : "/"}${url}`);
    return normalizePath(u.pathname);
  } catch {
    return null;
  }
}

const SOURCE_PRIORITY: Record<StaticRedirectRow["source"], number> = {
  manual: 4,
  wp_redirects: 3,
  sitemap_gap: 2,
  auto_wp: 1,
};

/** Laczy reguly z wielu zrodel; przy tej samej from_path wygrywa wyzszy priorytet. */
export function mergeRedirects(...groups: StaticRedirectRow[][]): StaticRedirectRow[] {
  const byFrom = new Map<string, StaticRedirectRow>();
  for (const group of groups) {
    for (const r of group) {
      const key = r.from_path;
      const cur = byFrom.get(key);
      if (!cur || SOURCE_PRIORITY[r.source] >= SOURCE_PRIORITY[cur.source]) byFrom.set(key, r);
    }
  }
  return [...byFrom.values()];
}

export interface RedirectsFileResult {
  content: string;
  staticCount: number;
  dynamicCount: number;
  dropped: StaticRedirectRow[];
  warnings: string[];
}

/**
 * Tresc pliku _redirects. Regula www -> apex ZAWSZE pierwsza. Reguly ponad
 * limit CF sa odrzucane (dropped) — najpierw auto_wp, potem sitemap_gap.
 */
export function renderRedirectsFile(rows: StaticRedirectRow[], domain: string): RedirectsFileResult {
  const warnings: string[] = [];
  const sorted = [...rows].sort((a, b) => SOURCE_PRIORITY[b.source] - SOURCE_PRIORITY[a.source]);
  const kept: StaticRedirectRow[] = [];
  const dropped: StaticRedirectRow[] = [];
  let staticCount = 0;
  let dynamicCount = 1; // www -> apex
  for (const r of sorted) {
    const dyn = isDynamicRule(r.from_path);
    if (dyn && dynamicCount >= CF_DYNAMIC_REDIRECTS_LIMIT) { dropped.push(r); continue; }
    if (!dyn && staticCount >= CF_STATIC_REDIRECTS_LIMIT) { dropped.push(r); continue; }
    if (dyn) dynamicCount++; else staticCount++;
    kept.push(r);
  }
  if (dropped.length) warnings.push(`Odrzucono ${dropped.length} regul ponad limit Cloudflare Pages (${CF_STATIC_REDIRECTS_LIMIT} statycznych / ${CF_DYNAMIC_REDIRECTS_LIMIT} dynamicznych).`);

  const lines: string[] = [
    `# Wygenerowane przez PBN-SD — migracja ${domain}`,
    `https://www.${domain}/* https://${domain}/:splat 301`,
  ];
  for (const r of kept) {
    const to = isDynamicRule(r.from_path) && r.from_path.endsWith("/*") && r.to_path.endsWith("/*")
      ? r.to_path.replace(/\*$/, ":splat")
      : r.to_path;
    lines.push(`${r.from_path} ${to} ${r.code}`);
  }
  return { content: `${lines.join("\n")}\n`, staticCount, dynamicCount, dropped, warnings };
}

/**
 * Naglowki HTTP (cache + bezpieczenstwo).
 *
 * BEZ `X-Frame-Options` — podglad w Studio (/strony/:websiteId) osadza strone
 * w <iframe> z pages.dev; strona statyczna nie ma sesji ani formularzy, wiec
 * clickjacking nie ma tu celu. NIE dodawac `Content-Security-Policy:
 * frame-ancestors https://dashboard...` — wypisaloby adres panelu w naglowkach
 * kazdego zaplecza (footprint). Test w redirectsBuilder.test.ts pilnuje.
 * Zostaja: X-Content-Type-Options, Referrer-Policy, Cache-Control.
 */
export function renderHeadersFile(): string {
  return [
    "/*",
    "  X-Content-Type-Options: nosniff",
    "  Referrer-Policy: strict-origin-when-cross-origin",
    "  Cache-Control: public, max-age=600",
    "",
    "/wp-content/uploads/*",
    "  Cache-Control: public, max-age=31536000, immutable",
    "",
    "/assets/*",
    "  Cache-Control: public, max-age=31536000, immutable",
    "",
  ].join("\n");
}

/** Czy sciezka jest pokryta regula (dokladnie albo przez prefiks /x/*). */
export function isPathRedirected(path: string, rows: StaticRedirectRow[]): boolean {
  const p = normalizePath(path);
  for (const r of rows) {
    if (r.from_path === p || r.from_path === p.replace(/\/$/, "")) return true;
    if (r.from_path.endsWith("/*")) {
      const prefix = r.from_path.slice(0, -1); // "/tag/"
      if (p.startsWith(prefix)) return true;
    }
    if (r.from_path.includes("*")) {
      const re = new RegExp(`^${r.from_path.split("*").map(escapeRe).join(".*")}$`);
      if (re.test(p)) return true;
    }
  }
  return false;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
